import React, { useEffect, useState, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { Typography, Grid, Button, Backdrop, CircularProgress, Snackbar } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import SubQuestionDetails from "./SubQuestionDetails";
import CommentInputs from "./CommentInputs";
import { useHistory } from "react-router-dom";
import { AppConfig, roles, SPECIAL_APPROVAL_QID } from "@configs/admin-config";
import { getAccessToken, checkIfRoleExists } from "@utils/oauth";
import EmailIcon from "@mui/icons-material/Email";

import parse from "html-react-parser";

const useStyles = makeStyles()((theme) => ({
  backdrop: {
    zIndex: 2,
    color: "white",
  },
  descriptionParent: {
    display: "flex",
  },

  descriptionIndex: {
    flexBasis: "4%",
    flexShrink: 0,
    marginRight: 8,
    "@media(max-width: 900px)": {
      flexBasis: "5%",
    },
    "@media(max-width: 600px)": {
      flexBasis: "10%",
    },
    "@media(max-width: 400px)": {
      flexBasis: "15%",
    },
  },
  descriptionDetails: {
    flexBasis: "96%",
    textAlign: "justify",
    "@media(max-width: 900px)": {
      flexBasis: "95%",
    },
    "@media(max-width: 600px)": {
      flexBasis: "90%",
    },
    "@media(max-width: 400p6x)": {
      flexBasis: "85%",
    },
  },
  buttonGrid: {
    marginTop: 5,
    marginBottom: 5,
    paddingLeft: "4.3%",
    "@media(max-width: 900px)": {
      paddingLeft: "5%",
    },
    "@media(max-width: 600px)": {
      paddingLeft: "10%",
    },
    "@media(max-width: 400px)": {
      paddingLeft: "15%",
    },
  },
  specialApprovalContainer: {
    paddingLeft: "4.3%",
    marginTop: 10,
    marginBottom: 10,
    display: "flex",
    justifyContent: "flex-end",
    "@media(max-width: 900px)": {
      paddingLeft: "5%",
    },
    "@media(max-width: 600px)": {
      paddingLeft: "10%",
    },
    "@media(max-width: 400px)": {
      paddingLeft: "15%",
    },
  },
  saveButton: {
    textTransform: "none",
  },
  approverSaveButton: {
     textTransform: "none",
     margin: "2px 5px",
   },
  editButton: {
    left: 15,
    textTransform: "none",
  },
  viewTRButton: {
    marginBottom: 10,
    marginTop: 10,
    textTransform: "none",
  },
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const QuestionDetails = (props) => {
  const { classes } = useStyles();
  const history = useHistory();
  const question = props.question;
  const subQuestions = props.subQuestions;
  const category = props.category;
  const files = props.files;
  const { ADMIN, TRADE_REFERENCE_FORM } = AppConfig.urls;

  const [backdropOpen, setbackDrop] = useState(false);
  const [fieldDisabled, setFieldDisabled] = useState(false);
  const [successSnack, setSuccess] = useState(false);
  const [errorSnack, setErrorSnack] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [isValid, setIsValid] = useState(false);
  const [isError, setError] = useState(false);
  const [financeResult, setFinanceResult] = useState("");
  const [specialApprovalResult, setSpecialApprovalResult] = useState("");
  const [isAuthorized, setAuthorized] = useState(true);
  const [specialApprovalRequested, setSpecialApprovalRequested] = useState(false);
  
  useEffect(() => {
    if ((question.questionId === 40 || question.questionId === 41) && checkIfRoleExists(roles.superuser)) {
      setAuthorized(true);
    } else if (question.questionId === 44 && checkIfRoleExists(roles.financeApprover)) {
      setAuthorized(true);
    } else if (question.questionId === SPECIAL_APPROVAL_QID && checkIfRoleExists(roles.financeSpecialApprover)) {
      setAuthorized(true);
    } else {
      setAuthorized(false);
    }

    if (props.specialApproval !== "") {
      setSpecialApprovalRequested(true);
    }
  });

  const filteredAnswers = useMemo(() => {
    if (question.questionId === SPECIAL_APPROVAL_QID) {
      return props.answers
        .filter((answer) => answer.questionId === SPECIAL_APPROVAL_QID)
        .map((answer) => ({
          subQuestionId: answer.subQuestionId,
          questionId: answer.questionId,
          booleanAnswer: !!answer.booleanAnswer,
          descriptionAnswer: answer.descriptionAnswer,
          companyId: answer.companyId.toString(),
        }));
    }
  }, [props.answer]);

  useEffect(() => {
    if (filteredAnswers && filteredAnswers.length > 0) {
      setAnswers(filteredAnswers);
    }
  }, [filteredAnswers]);

  const callback = (subQuestionId, booleanAnswer, descriptionAnswer, uploadFiles, isValid) => {
    if (subQuestionId === 44 && booleanAnswer === true) {
      setFinanceResult("approved");
    } else if (subQuestionId === 44 && booleanAnswer === false) {
      setFinanceResult("rejected");
    } else if (subQuestionId === 98 && booleanAnswer === true) {
      setSpecialApprovalResult("approved");
    } else if (subQuestionId === 98 && booleanAnswer === false) {
      setSpecialApprovalResult("rejected");
    }

    setIsValid(isValid);

    setAnswers(answers.filter((item) => item.subQuestionId !== subQuestionId));

    if (isValid) {
      setError(false);
      setAnswers((prev) => [
        ...prev,
        {
          subQuestionId: subQuestionId,
          questionId: question.questionId,
          booleanAnswer: Boolean(booleanAnswer),
          descriptionAnswer: descriptionAnswer,
          companyId: props.companyId,
        },
      ]);
    }
  };

  const saveData = async () => {
    if (question.questionId === SPECIAL_APPROVAL_QID && answers.length < 2) {
      setIsValid(false);
      setError(true);
    } else if (isValid) {
      setbackDrop(true);
      setFieldDisabled(true);
      const requestURL = AppConfig.baseUrl + AppConfig.financeFeedback;

      const data = {
        answers: answers,
        financeResult: financeResult,
        specialApprovalResult: specialApprovalResult,
      };

      try {
        let response = await fetch(requestURL, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + getAccessToken(),
          },
          body: JSON.stringify(data),
        });
        let body = await response.json();
        if (response.status === 200) {          
          setSuccess(true);
          setIsValid(false);        
          props.formRender();
        } else {
          console.error(body.message);
          setErrorSnack(true);
        }
        setbackDrop(false);
        return null;
      } catch (error) {
        setbackDrop(false);
        console.error(error);
        return error;
      }
    } else {
      setError(true);
    }
  };

  const editField = () => {
    setFieldDisabled(false);
  };

  const viewTradeReference = async (index) => {
    setbackDrop(true);
    const requestURL = AppConfig.baseUrl + AppConfig.tradeReferenceData + props.companyId + "/" + AppConfig.linkData;

    try {
      let response = await fetch(requestURL, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
      });
      let body = await response.json();

      if (response.status === 200) {
        if (body && body.length > 0) {
          if (body[index - 1] && body[index - 1].linkId) {
            setbackDrop(false);
            const tradeReferenceUrl = `${ADMIN}${TRADE_REFERENCE_FORM}${props.companyId}/${body[index - 1].linkId}`;
            window.open(tradeReferenceUrl, '_blank');
          }
        } else {
          setbackDrop(false);
          setErrorSnack(true);
          console.warn("Not retrieved any data from backend");
        }
      } else {
        console.error(body.message);
      }

      return null;
    } catch (error) {
      console.error(error);
      return error;
    }
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSuccess(false);
    setErrorSnack(false);
  };

  const disableCall = (isDisable, answer) => {
    if (answer.length > 0) {
      setFieldDisabled(true);
    } else {
      setFieldDisabled(isDisable);
    }
  };

  const sendSpecialApprovalEmail = (companyId) => async (e) => {
    setbackDrop(true);
    const requestURL = AppConfig.baseUrl + AppConfig.sendSpecialApprovalEmail;
    try {
      let response = await fetch(requestURL, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
        body: companyId,
      });
      setbackDrop(false);
      if (response.status === 200) {
        setSuccess(true);
        setSpecialApprovalRequested(true);
      } else {
        throw new Error("Failed to send special approval email");
      }
      return null;
    } catch (error) {
      setErrorSnack(true);
      return error;
    } finally {
      window.location.reload();
    }
  };

  return (
    <React.Fragment>
      <Backdrop open={backdropOpen} className={classes.backdrop}>
        <CircularProgress color="secondary" />
      </Backdrop>
      <Snackbar anchorOrigin={{ vertical: "bottom", horizontal: "center" }} open={successSnack} autoHideDuration={3000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="success">
          Success!
        </Alert>
      </Snackbar>
      <Snackbar anchorOrigin={{ vertical: "bottom", horizontal: "center" }} open={errorSnack} autoHideDuration={3000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="error">
          Failed!
        </Alert>
      </Snackbar>

      {category.categoryId === question.categoryId && (
        <React.Fragment>
          <Grid item xs={12}>
            <div className={classes.descriptionParent}>
              <Typography className={classes.descriptionIndex}></Typography>
              <Typography variant="body2" className={classes.descriptionDetails}>
                {parse(question.questionHeading)}
              </Typography>
            </div>
          </Grid>
          <Grid item xs={12} className={classes.verticalcenter}>
            <div className={classes.descriptionParent}>
              <Typography className={classes.descriptionIndex}>
                {category.sortIndex - 2}.{question.sortIndex}
              </Typography>
              <Typography className={classes.descriptionDetails}>{parse(question.questionDescription)}</Typography>
              {question.questionId === 44 && checkIfRoleExists(roles.finance) && props.sendApprovalEmail && (
                <Grid
                  item
                  xs={12}
                  md={6}
                  lg={12}
                  className={classes.buttonGrid}
                  style={{ display: "flex", justifyContent: "flex-end"}}
                >
                  <Button
                    className={classes.saveButton}
                    variant="contained"
                    color="secondary"
                    size="small"
                    onClick={props.sendApprovalEmail}
                    disabled={props.approvalEmailSent !== false}
                    startIcon={props.approvalEmailSent === false ? <EmailIcon /> : undefined}
                  >
                    {props.approvalEmailSent === false ? "Send for approval" : "Sent for approval"}
                  </Button>
                </Grid>
              )}
            </div>
            {category.categoryId === 3 && props.selectedRadio === "references" && (
              <div className={classes.descriptionParent}>
                <Typography className={classes.descriptionIndex}></Typography>
                <div className={classes.descriptionDetails}>
                  <Button
                    className={classes.viewTRButton}
                    variant="outlined"
                    color="primary"
                    disabled={!fieldDisabled}
                    size="small"
                    onClick={() => viewTradeReference(question.sortIndex)}
                  >
                    View Trade Reference{" "}
                  </Button>
                </div>
              </div>
            )}
          </Grid>
          {subQuestions.length > 0 &&
            subQuestions.map((subQuestion) => {
              return subQuestion.questionId === question.questionId ? (
                <SubQuestionDetails
                  key={subQuestion.subQuestionId}
                  category={category}
                  question={question}
                  subQuestion={subQuestion}
                  files={files}
                  parentCallback={callback}
                  formCompleted={props.formCompleted}
                  isSubmitted={props.isSubmitted}
                  isDrafted={props.isDrafted}
                  disableCall={disableCall}
                  isError={isError}
                  fieldDisabled={isAuthorized ? fieldDisabled : true}                 
                  answer={
                    props.answers &&
                    props.answers.filter((answer) => answer.subQuestionId === subQuestion.subQuestionId)
                  }
                  comments={props.comments}
                  approvalEmailSent={props.approvalEmailSent}
                  email={props.email}
                  companyId={props.companyId}
                />
              ) : null;
            })}
          {(question.questionId === 42 || question.questionId === 43) && (
            <Grid item xs={12}>
              <CommentInputs
                questionId={question.questionId}
                companyId={props.companyId}
                comments={props.comments}
                formRender={props.formRender}
              />
            </Grid>
          )}
          {((category.categoryId === 7 || category.categoryId === 8) &&
            question.questionDescription !== "Creator" &&
            question.questionDescription !== "Reviewer" &&
            isAuthorized) ||
          (question.questionId === SPECIAL_APPROVAL_QID && checkIfRoleExists(roles.financeSpecialApprover)) ? (
            <div className={classes.buttonGrid}>
              <Button
                className={classes.approverSaveButton}
                variant="contained"
                color="secondary"
                disabled={fieldDisabled || !isValid}
                size="small"
                onClick={saveData}
              >
                Save
              </Button>
              <Button
                className={classes.editButton}
                variant="outlined"
                color="primary"
                disabled={!fieldDisabled}
                size="small"
                onClick={editField}
              >
                Edit
              </Button>
            </div>
          ) : null}
          {question.questionId === 44 && checkIfRoleExists(roles.financeApprover) && (
            <Grid item xs={12} className={classes.specialApprovalContainer}>
              <Button
                className={classes.saveButton}
                variant="contained"
                color="secondary"
                size="small"
                onClick={!specialApprovalRequested ? sendSpecialApprovalEmail(props.companyId) : undefined}
                disabled={specialApprovalRequested}
              >
                {specialApprovalRequested ? "Special Approval Requested" : "Request Special Approval"}
              </Button>
            </Grid>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default QuestionDetails;
